import type Luminary from '@lumen5/luminary';
import type { GetRootState } from 'js/src/libs/RootState';

import { checkCaptionOverlap } from './checks/captionOverlap';
import { checkDuplicateText } from './checks/duplicateText';
import { checkNoScenes } from './checks/noScenes';
import { checkOverlayCoverage } from './checks/overlayCoverage';
import { checkVoicedrivenSceneReadingTime } from './checks/voicedrivenSceneReadingTime';
import { checkPlaceholderIcon } from './checks/placeholderIcon';
import { checkPlaceholderText } from './checks/placeholderText';
import { checkQuoteDesignWithoutAttribution } from './checks/quoteDesignWithoutAttribution';
import { checkSceneReadingTime } from './checks/sceneReadingTime';
import { checkTextClipping } from './checks/textClipping';
import { checkTextContrast } from './checks/textContrast';
import { checkTextWidows } from './checks/textWidows';
import { checkVoiceGroupMissingTranscript } from './checks/voiceGroupMissingTranscript';

import { ErrorReporter } from 'js/src/libs/error-reporter';

export type LintSeverity = 'error' | 'warning';

export type LintCheckName =
    | 'no_scenes'
    | 'voice_group_missing_transcript'
    | 'overlay_coverage'
    | 'caption_overlap'
    | 'text_contrast'
    | 'text_clipping'
    | 'text_widows'
    | 'duplicate_text'
    | 'placeholder_text'
    | 'placeholder_icon'
    | 'quote_design_without_attribution'
    | 'scene_reading_time'
    | 'voicedriven_scene_reading_time';

export interface LintFinding {
    check: LintCheckName;
    severity: LintSeverity;
    /**
     * The stable id of the scene the finding is about, when it targets a specific scene. The lint
     * report shows the short form of this id so the agent can address the scene directly with the
     * same scene_id the edit tools (set_text/set_media/set_icon) take — never a positional index,
     * which drifts as scenes are added (AP-931). Scene-group-level findings use sceneGroupIndex
     * instead, since scene groups are still addressed by index.
     */
    sceneId?: string;
    sceneGroupIndex?: number;
    message: string;
}

export interface LintReport {
    findings: LintFinding[];
    checksRun: LintCheckName[];
}

/**
 * A single lint check. Operates on one Luminary tree and returns the findings
 * it produced. Async because some checks (e.g. text contrast) render frames.
 */
export type LintCheck = (
    luminary: Luminary,
    getState: GetRootState,
) => LintFinding[] | Promise<LintFinding[]>;

const CHECKS: Record<LintCheckName, LintCheck> = {
    no_scenes: checkNoScenes,
    voice_group_missing_transcript: checkVoiceGroupMissingTranscript,
    overlay_coverage: checkOverlayCoverage,
    caption_overlap: checkCaptionOverlap,
    text_contrast: checkTextContrast,
    text_clipping: checkTextClipping,
    text_widows: checkTextWidows,
    duplicate_text: checkDuplicateText,
    placeholder_text: checkPlaceholderText,
    placeholder_icon: checkPlaceholderIcon,
    quote_design_without_attribution: checkQuoteDesignWithoutAttribution,
    scene_reading_time: checkSceneReadingTime,
    voicedriven_scene_reading_time: checkVoicedrivenSceneReadingTime,
};

export const ALL_LINT_CHECKS = Object.keys(CHECKS) as LintCheckName[];

/**
 * Runs the configured battery of read-only quality checks against a single
 * Luminary tree and returns one combined report. Each check is isolated: a
 * check that throws is skipped rather than failing the whole run, so one
 * broken check never hides the findings of the others.
 */
export async function runVideoLint(
    luminary: Luminary,
    getState: GetRootState,
    options: { only?: LintCheckName[] } = {},
): Promise<LintReport> {
    const checksToRun = options.only?.length ? options.only : ALL_LINT_CHECKS;
    const findings: LintFinding[] = [];

    for (const checkName of checksToRun) {
        const check = CHECKS[checkName];
        if (!check) {
            continue;
        }
        try {
            const checkFindings = await check(luminary, getState);
            findings.push(...checkFindings);
        } catch (error) {
            ErrorReporter.capture(error, {
                level: 'warning',
                extra: { message: `Lint check "${checkName}" failed` },
            });
        }
    }

    return { findings, checksRun: checksToRun };
}
